import React, { useRef } from "react";
import html2pdf from "html2pdf.js";

/**
 * Props:
 *  invoice: object from backend
 *  allowDownload: boolean to show download button
 */
export default function InvoiceTemplate({ invoice, allowDownload }) {
  const templateRef = useRef(null);

  const handleDownloadPDF = () => {
    if (!templateRef.current) return;

    // Apply PDF-specific styles
    const yellowHeaders = templateRef.current.querySelectorAll(
      ".bg-yellow-400 span"
    );
    yellowHeaders.forEach((header) => {
      header.style.position = "relative";
      header.style.top = "-3px";
    });

    // Fix table row positioning
    const tableRows = templateRef.current.querySelectorAll(
      "tbody tr"
    );
    tableRows.forEach((row) => {
      row.style.position = "relative";
      row.style.top = "-2px";
    });



    const opt = {
      margin: 0,
      filename: `${invoice.invoiceNo || "invoice"}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 3, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "p" },
    };

    html2pdf()
      .set(opt)
      .from(templateRef.current)
      .save()
      .then(() => {
        // Reset styles after PDF generation
        yellowHeaders.forEach((header) => {
          header.style.position = "";
          header.style.top = "";
        });
        tableRows.forEach((row) => {
          row.style.position = "";
          row.style.top = "";
        });

      });
  };

  const inv = invoice || {};
  const billed = inv.billedTo || {};
  const loanSummary = inv.loanSummary || [];
  const fees = inv.fees || [];
  const totals = inv.totals || {};

  return (
    <>
      {allowDownload && (
        <div className="flex justify-end mb-2">
          <button
            onClick={handleDownloadPDF}
            className="px-4 py-2 bg-yellow-400 text-black font-semibold rounded"
          >
            Download PDF
          </button>
        </div>
      )}

      {/* Fixed width for PDF (A4 width = 794px at 96dpi) */}
      <div
        ref={templateRef}
        className="bg-white mx-auto"
        style={{ width: "794px" }}
      >
        {/* Header - black band with LONO */}
        <div className="bg-gray-900 text-white p-6 rounded-t w-full">
          <div className="relative flex justify-center items-center">
            <div className="text-6xl font-bold tracking-tight mb-2">LONO</div>
            <div className="absolute right-0">
              <img
                src="/assets/lono_logo.jpg"
                alt="LONO Logo"
                className="w-16 h-16 rounded-full object-cover border-2 border-gray-300"
              />
            </div>
          </div>
          <div className="text-center">
            <div className="text-yellow-400 uppercase font-semibold">
              Fast & Secure Lending
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto bg-white p-6 border rounded shadow-sm">
          {/* Content */}
          <div className="mt-6 text-black">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-3xl font-extrabold">INVOICE</h2>
                <div className="mt-3">
                  <h4 className="text-md font-extrabold">Billed To:</h4>
                  <div className="text-lg font-semibold">{billed.name}</div>
                  <div className="text-sm text-gray-600">{billed.address}</div>
                  <div className="text-sm text-gray-600">{billed.phone}</div>
                  <div className="text-sm text-gray-600">{billed.email}</div>
                </div>
              </div>

              <div className="text-right text-black">
                <div className="text-lg">
                  <strong>Invoice No.:</strong> {inv.invoiceNo}
                </div>
                <div className="text-lg">
                  <strong>Invoice Date:</strong>{" "}
                  {inv.invoiceDate
                    ? new Date(inv.invoiceDate).toLocaleDateString("en-GB")
                    : ""}
                </div>
                <div className="text-lg">
                  <strong>Due Date:</strong>{" "}
                  {inv.dueDate
                    ? new Date(inv.dueDate).toLocaleDateString("en-GB")
                    : ""}
                </div>
              </div>
            </div>

            {/* Loan & EMI summary */}
            <div className="mt-6">
              <div
                className="bg-yellow-400 w-full font-bold"
                style={{ padding: "12px 16px" }}
              >
                <span>LOAN & EMI SUMMARY</span>
              </div>
              <table className="w-full mt-3 text-left">
                <thead className="bg-white text-black font-semibold">
                  <tr className="border-b border-gray-400">
                    <th className="py-2">Loan Type</th>
                    <th>Amount</th>
                    <th>Tenure</th>
                    <th>ROI</th>
                    <th>EMI</th>
                  </tr>
                </thead>
                <tbody>
                  {loanSummary.map((l, idx) => (
                    <tr key={idx} className="border-b text-black">
                      <td className="py-3">{l.loanType}</td>
                      <td> ₹{Number(l.amount || 0).toLocaleString("en-IN")}</td>
                      <td>{l.tenure} Years</td>
                      <td>{l.roi}%</td>
                      <td> ₹{Number(l.emi || 0).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Fee & Charges Breakdown */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <div
                  className="bg-yellow-400 w-full font-bold text-black"
                  style={{ padding: "12px 16px" }}
                >
                  <span>FEE & CHARGES BREAKDOWN</span>
                </div>

                <table className="w-full mt-3 text-left text-black border-collapse">
                  <thead className="bg-white text-black font-semibold">
                    <tr className="border-b border-gray-400 ">
                      <th className="py-2">Description</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
<tbody>
  {fees.map((f, idx) => (
    <tr
      key={idx}
      className={idx === fees.length - 1 ? "border-b border-gray-300" : ""}
    >
      <td className="py-3">{f.description}</td>
      <td>
        ₹{Number(f.amount || 0).toLocaleString("en-IN")}
      </td>
      <td>
        <div className="flex items-center space-x-2">
          {/* Circle stays neutral */}
          <div className="w-5 h-5 rounded-full flex items-center justify-center bg-white">
            <span
              className={`text-xs font-bold ${
                f.status?.toLowerCase() === "paid"
                  ? "text-green-600"
                  : f.status?.toLowerCase() === "pending"
                  ? "text-yellow-600"
                  : f.status?.toLowerCase() === "overdue"
                  ? "text-red-600"
                  : "text-gray-600"
              }`}
            >
              ✔
            </span>
          </div>

          {/* Status text */}
          <span
            className={`font-semibold ${
              f.status?.toLowerCase() === "paid"
                ? "text-green-700"
                : f.status?.toLowerCase() === "pending"
                ? "text-yellow-700"
                : f.status?.toLowerCase() === "overdue"
                ? "text-red-700"
                : "text-gray-700"
            }`}
          >
            {f.status}
          </span>
        </div>
      </td>
    </tr>
  ))}
</tbody>

                </table>

                {/* Totals */}
                <div className="mt-4 text-black">
                  <div className="flex justify-between">
                    <div className="font-semibold">Total Invoice Value:</div>
                    <div className="font-semibold">
                      ₹
                      {Number(totals.totalInvoiceValue || 0).toLocaleString(
                        "en-IN"
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between mt-1">
                    <div className="font-semibold">Paid:</div>
                    <div className="font-semibold">
                      ₹{Number(totals.paid || 0).toLocaleString("en-IN")}
                    </div>
                  </div>
                  <div className="flex justify-between mt-1">
                    <div className="font-semibold">Balance Due:</div>
                    <div className="font-semibold">
                      ₹{Number(totals.balanceDue || 0).toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
              </div>

              {/* QR + Balance Due */}
              <div className="md:col-span-1 text-center">
                <h2 className="text-black font-bold text-xl mb-4">
                  Scan & Pay
                </h2>
                {inv.qrFile ? (
                  <img
                    src={inv.qrFile}
                    alt="qr"
                    className="w-35 h-35 object-contain mx-auto mb-4"
                  />
                ) : (
                  <div
                    className="w-35 h-35 bg-gray-100 mx-auto mb-4"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span className="text-sm text-gray-400">QR</span>
                  </div>
                )}

                <div className="text-xl font-bold mb-2">Balance Due</div>
                <div className="text-3xl font-extrabold text-gray-900 mb-6">
                  ₹{Number(totals.balanceDue || 0).toLocaleString("en-IN")}
                </div>

                <div className="text-5xl font-bold tracking-tight mb-2">
                  LONO
                </div>
                <div
                  className="text-xs text-gray-500 uppercase font-semibold"
                  style={{ fontSize: "8px" }}
                >
                  We save your money and time
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
